import { useEffect, useMemo, useState } from "react";
import type { GroupView, Member } from "@/services/types";
import { groupService, memberService } from "@/services";
import { useToast } from "@/context/ToastContext";
import { useConfirm } from "@/context/ConfirmContext";
import { Modal } from "@/components/base/Modal";
import { Button } from "@/components/base/Button";
import { Avatar } from "@/components/base/Avatar";
import { Badge } from "@/components/base/Badge";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { CreateMemberModal } from "./CreateMemberModal";

interface AssignModalProps {
  open: boolean;
  group: GroupView;
  seat: number;
  onClose: () => void;
  onAssigned: () => void;
}

export function AssignModal({ open, group, seat, onClose, onAssigned }: AssignModalProps) {
  const toast = useToast();
  const confirm = useConfirm();
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState<Member[]>([]);
  const [query, setQuery] = useState("");
  const [assigning, setAssigning] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    memberService
      .listMembers()
      .then((m) => {
        if (active) setMembers(m);
      })
      .catch(() => {})
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open]);

  const inGroupIds = useMemo(() => new Set(group.tickets.map((t) => t.memberId)), [group.tickets]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return members;
    return members.filter(
      (m) =>
        m.fullName.toLowerCase().includes(q) ||
        m.memberCode.toLowerCase().includes(q) ||
        m.primaryPhone.includes(q)
    );
  }, [members, query]);

  const doAssign = async (member: Member) => {
    if (assigning) return;
    // Inactive members are allowed but require explicit confirmation.
    if (member.status === "inactive") {
      const ok = await confirm({
        title: "Assign an inactive member?",
        message: `${member.fullName} is marked inactive. They can still be enrolled, but please confirm you intend to add them.`,
        confirmLabel: "Assign anyway",
      });
      if (!ok) return;
    }
    setAssigning(member.id);
    try {
      await groupService.addTicket(group.id, member.id, seat);
      toast.success("Ticket assigned", `${member.fullName} now holds ticket #${seat}.`);
      onClose();
      onAssigned();
    } catch (err) {
      toast.error("Could not assign member", err instanceof Error ? err.message : "Please try again.");
    } finally {
      setAssigning(null);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Assign ticket #${seat}`}
      description="Pick a member to hold this ticket. Search by name, code or phone."
      size="lg"
    >
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-full sm:w-80">
            <i className="ri-search-line pointer-events-none absolute inset-y-0 left-0 flex w-9 items-center justify-center text-base text-foreground-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search members…"
              className="h-9 w-full rounded-md border border-background-300 bg-background-50 pl-9 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400/40"
            />
            {loading ? (
              <span className="pointer-events-none absolute inset-y-0 right-0 flex w-8 items-center justify-center text-primary-600">
                <i className="ri-loader-4-line animate-spin text-sm" />
              </span>
            ) : query ? (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => setQuery("")}
                className="absolute inset-y-0 right-0 flex w-8 cursor-pointer items-center justify-center text-foreground-400 hover:text-foreground-700"
              >
                <i className="ri-close-line text-base" />
              </button>
            ) : null}
          </div>
          <Button variant="outline" icon={<i className="ri-user-add-line" />} onClick={() => setCreateOpen(true)}>
            Create new member
          </Button>
        </div>

        <div className="max-h-80 overflow-auto rounded-md border border-background-200">
          {filtered.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-foreground-500">No members match your search.</p>
          ) : (
            <ul className="divide-y divide-background-100">
              {filtered.map((m) => {
                const already = inGroupIds.has(m.id);
                const blacklisted = m.status === "blacklisted";
                const inactive = m.status === "inactive";
                const disabled = already || blacklisted;
                const otherCount = m.enrollments.length;
                return (
                  <li key={m.id} className="flex items-center gap-3 px-3 py-2.5">
                    <Avatar name={m.fullName} size="sm" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-foreground-900">{m.fullName}</p>
                      <p className="tnum truncate text-xs text-foreground-500">
                        {m.memberCode} · {m.primaryPhone}
                        {m.city ? ` · ${m.city}` : ""}
                      </p>
                      {otherCount > 0 ? (
                        <p className="mt-0.5 text-xs text-secondary-700">
                          Already in {otherCount} other group{otherCount === 1 ? "" : "s"} · total outstanding{" "}
                          <CurrencyDisplay amount={m.enrollments.reduce((s, e) => s + e.outstanding, 0)} hideSymbol />
                        </p>
                      ) : null}
                    </div>
                    <div className="flex flex-none items-center gap-2">
                      {blacklisted ? <Badge variant="cancelled" dot>Blacklisted</Badge> : null}
                      {inactive ? <Badge variant="inactive" dot>Inactive</Badge> : null}
                      {already ? (
                        <Badge color="secondary">In this group</Badge>
                      ) : (
                        <Button size="sm" loading={assigning === m.id} disabled={disabled} onClick={() => doAssign(m)}>
                          Assign
                        </Button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <p className="text-xs text-foreground-500">
          Blacklisted members are blocked from enrolment. Inactive members can be added but require confirmation.
        </p>
      </div>

      <CreateMemberModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(member) => {
          setQuery("");
          doAssign(member);
        }}
      />
    </Modal>
  );
}